import { createQuery } from "@/api/utils";
import { camelCased } from "@/utils/misc";
import {
    FinanceLabField,
    FinanceLabMeta,
    FinanceLabMetric,
    FinanceLabStatus,
} from "./FinanceLab.model";
import { PeriodKind } from "../Labeling/Account.model";
import { QueryStringBuilder } from "@/utils/filtering/QueryStringBuilder";
import { formatFinanceLabValue } from "./FinanceLab.format";

const parseMeta = (metaString: string): FinanceLabMeta => {
    try {
        return camelCased(JSON.parse(metaString));
    } catch (error) {
        try {
            const jsonString = metaString.replace(/'/g, '"');
            return camelCased(JSON.parse(jsonString));
        } catch {
            return {};
        }
    }
};

export const getTaskStatus = createQuery<
    {
        id: string;
        status: FinanceLabStatus;
        state: FinanceLabStatus;
        meta: FinanceLabMeta;
    },
    { taskId: string }
>(({ api, params }) => {
    return api
        .get(`/hum-finance-lab/v1/finance_fields/task_status/${params.taskId}`)
        .then((res) => {
            return {
                ...res,
                meta: parseMeta(res.meta as string),
            };
        });
});

export const getFinanceLabMetrics = createQuery<
    FinanceLabMetric[],
    {
        labels: FinanceLabField[];
        periodKind?: PeriodKind;
        resampleTransform?: string;
        to?: string;
        from?: string;
    }
>(async ({ api, params, router }) => {
    const queryString = new QueryStringBuilder()
        .add("period_kind", params.periodKind)
        .add("value_kind", "plain")
        .add("resample_transform", params.resampleTransform)
        .add("to_", params.to)
        .add("from_", params.from);

    params.labels.forEach((label) => {
        queryString.add("label", label);
    });

    const response = await api.get(
        `/hum-finance-lab/v1/companies/${router.params.companyId}/metrics?${queryString.build()}`,
    );

    const metrics = response.data as FinanceLabMetric[];

    if (!metrics || !Array.isArray(metrics)) {
        return [];
    }

    return metrics.map((metric) => ({
        ...metric,
        values:
            metric.values?.map((v) => ({
                ...v,
                formattedValue: formatFinanceLabValue(
                    v.value,
                    metric.description?.fieldType,
                ),
            })) || [],
    }));
});
